import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Eye, Plus, UserX, UserCheck, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { api } from "@/api";
import { errMsg } from "@/utils";
import { toast } from "sonner";
import AdminLayout from "@/components/admin/AdminLayout";
import AdminGuard from "@/components/admin/AdminGuard";
import PageHeader from "@/components/admin/PageHeader";
import StatusBadge from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger
} from "@/components/ui/dialog";

const PAGE_SIZE = 20;

const EMPTY_FORM = { name: "", email: "", phone: "", bio: "", experience_yrs: 0, languages: "", specialization: "" };

export default function AdminAdvisors() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const loadAdvisors = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (debouncedSearch) params.search = debouncedSearch;
      const { data } = await api.get("/advisors", { params });
      setItems(data.advisors);
      setTotal(data.total);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }, [page, debouncedSearch]);

  useEffect(() => { loadAdvisors(); }, [loadAdvisors]);

  const createAdvisor = async () => {
    if (!form.name || !form.email) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      await api.post("/advisors", {
        ...form,
        experience_yrs: Number(form.experience_yrs) || 0,
        languages: form.languages.split(",").map(s => s.trim()).filter(Boolean),
        specialization: form.specialization.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Advisor created");
      setAddOpen(false);
      setForm(EMPTY_FORM);
      loadAdvisors();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  const toggleActive = async (adv) => {
    try {
      await api.patch(`/advisors/${adv.advisor_id}`, { is_active: !adv.is_active });
      toast.success(`Advisor ${adv.is_active ? "deactivated" : "activated"}`);
      loadAdvisors();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <AdminGuard>
      <AdminLayout>
        <PageHeader
          title="Advisors"
          subtitle={`${total} advisors`}
          actions={
            <Dialog open={addOpen} onOpenChange={setAddOpen}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[hsl(var(--zt-gold))] text-white hover:bg-[hsl(var(--zt-gold))]/90 rounded-full h-9 px-5 font-heading uppercase tracking-wider text-xs">
                  <Plus size={13} className="mr-1.5" /> Add Advisor
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-display text-[hsl(var(--zt-teal))]">New Advisor</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  {[
                    { key: "name", label: "Full Name *", type: "text" },
                    { key: "email", label: "Email *", type: "email" },
                    { key: "phone", label: "Phone", type: "text" },
                    { key: "experience_yrs", label: "Years of Experience", type: "number" },
                    { key: "languages", label: "Languages (comma-separated)", type: "text" },
                    { key: "specialization", label: "Specialization (comma-separated)", type: "text" },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1">{f.label}</label>
                      <input
                        type={f.type}
                        value={form[f.key]}
                        onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                        className="w-full h-9 px-3 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-lg focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30"
                      />
                    </div>
                  ))}
                  <div>
                    <label className="block text-[10px] font-heading uppercase tracking-wider text-[hsl(var(--zt-charcoal))]/55 mb-1">Bio</label>
                    <textarea
                      value={form.bio}
                      onChange={e => setForm(p => ({ ...p, bio: e.target.value }))}
                      className="w-full h-20 px-3 py-2 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-lg focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 resize-none"
                    />
                  </div>
                  <Button onClick={createAdvisor} disabled={saving} className="w-full bg-[hsl(var(--zt-teal))] text-white rounded-xl h-10 font-heading uppercase tracking-wider text-xs">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : "Create Advisor"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          }
        />

        {/* Search */}
        <div className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-4 mb-6 shadow-sm">
          <div className="relative max-w-md">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--zt-charcoal))]/40" />
            <input
              type="text"
              placeholder="Search advisors…"
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-4 h-9 text-sm border border-[hsl(var(--zt-beige-deep))] rounded-xl focus:outline-none focus:ring-1 focus:ring-[hsl(var(--zt-teal))]/30 bg-[hsl(var(--zt-beige))]/50"
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-6 h-40 animate-pulse" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-dashed border-[hsl(var(--zt-teal))]/20 p-16 text-center">
            <p className="text-sm text-[hsl(var(--zt-charcoal))]/40">No advisors found</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(adv => (
              <div key={adv.advisor_id} className="bg-white rounded-2xl border border-[hsl(var(--zt-beige-deep))] p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl bg-[hsl(var(--zt-teal))]/8 border border-[hsl(var(--zt-teal))]/15 grid place-items-center text-[hsl(var(--zt-teal))] font-bold text-sm flex-shrink-0">
                    {adv.name.charAt(0)}
                  </div>
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-[hsl(var(--zt-teal))] truncate">{adv.name}</p>
                    <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/55 truncate">{adv.email}</p>
                  </div>
                  <StatusBadge status={adv.is_active ? "active" : "inactive"} className="ml-auto flex-shrink-0" />
                </div>
                {adv.specialization?.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {adv.specialization.slice(0, 3).map(s => (
                      <span key={s} className="text-[9px] font-heading uppercase tracking-wider bg-[hsl(var(--zt-beige))] text-[hsl(var(--zt-charcoal))]/60 px-2 py-0.5 rounded-full">{s}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center gap-2 pt-3 border-t border-[hsl(var(--zt-beige-deep))]">
                  <p className="text-[11px] text-[hsl(var(--zt-charcoal))]/50 flex-1">{adv.experience_yrs}y exp</p>
                  <button onClick={() => navigate(`/admin/advisors/${adv.advisor_id}`)} title="View"
                    className="w-7 h-7 rounded-lg bg-[hsl(var(--zt-beige))] grid place-items-center text-[hsl(var(--zt-teal))]/60 hover:text-[hsl(var(--zt-teal))] transition-colors">
                    <Eye size={13} />
                  </button>
                  <button onClick={() => toggleActive(adv)} title={adv.is_active ? "Deactivate" : "Activate"}
                    className={`w-7 h-7 rounded-lg grid place-items-center transition-colors ${adv.is_active ? "bg-amber-50 text-amber-600 hover:bg-amber-100" : "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"}`}>
                    {adv.is_active ? <UserX size={13} /> : <UserCheck size={13} />}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalPages > 1 && (
          <div className="mt-6 flex items-center justify-center gap-4">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="w-8 h-8 rounded-lg border border-[hsl(var(--zt-beige-deep))] grid place-items-center disabled:opacity-40">
              <ChevronLeft size={14} />
            </button>
            <span className="text-xs font-heading font-semibold text-[hsl(var(--zt-teal))]">{page} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="w-8 h-8 rounded-lg border border-[hsl(var(--zt-beige-deep))] grid place-items-center disabled:opacity-40">
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </AdminLayout>
    </AdminGuard>
  );
}
